import _ from 'lodash'
import { named, withDependencies } from '@wix/thunderbolt-ioc'
import {
	BrowserWindowSymbol,
	IPropsStore,
	Props,
	StructureAPI,
	IStructureAPI,
	IPageWillMountHandler,
	MasterPageFeatureConfigSymbol,
	PageFeatureConfigSymbol,
	ITestApi,
	BrowserWindow,
} from '@wix/thunderbolt-symbols'
import type { TestApiPageConfig } from './types'
import { name } from './symbols'
import { isSSR } from '@wix/thunderbolt-commons'

export const TestApi = withDependencies(
	[
		named(PageFeatureConfigSymbol, name),
		named(MasterPageFeatureConfigSymbol, name),
		BrowserWindowSymbol,
		Props,
		StructureAPI,
	],
	(
		pageConfig: TestApiPageConfig,
		masterPageConfig: TestApiPageConfig,
		window: NonNullable<BrowserWindow>,
		propsStore: IPropsStore,
		structureAPI: IStructureAPI
	): IPageWillMountHandler => ({
		name: 'testApi',
		pageWillMount: () => {
			if (isSSR(window)) {
				return
			}

			const connections = _.mergeWith(
				{},
				pageConfig.connections,
				masterPageConfig.connections,
				(objValue, srcValue) => (_.isArray(objValue) ? objValue.concat(srcValue) : undefined)
			)

			const getCompIdByRole = (role: string, controllerCompId: string) => {
				if (!controllerCompId) {
					throw new Error(`No controller with id: ${controllerCompId}`)
				}

				const compId = connections[controllerCompId]?.[role]?.[0]?.compId
				if (!compId) {
					throw new Error(`No component controlled by ${controllerCompId} via role: ${role}`)
				}
				return compId
			}

			const domSelectors: ITestApi['domSelectors'] = {
				getCompNicknameById: (compId) => {
					const nickNames = _.flatMap(connections.wixCode)
					const comp = _.find(nickNames, { compId })
					if (!comp) {
						throw new Error(`No component with id: ${compId}`)
					}
					return comp.role
				},
				getCompIdByNickname: (nickname) => {
					return getCompIdByRole(nickname, 'wixCode')
				},
				getCompIdByRole,
				getElementsByCompTypeAndProps: (container, componentType, props) => {
					const compProps = _.chain(structureAPI.getEntireStore())
						.pickBy((comp) => comp.componentType.toLowerCase() === componentType.toLowerCase())
						.mapValues((comp, compId) => propsStore.get(compId))
						.value()
					return _.chain(compProps)
						.pickBy((currentProps, compId) => {
							const isPropsEquals = _.isEqual(_.pick(currentProps, _.keys(props)), props)
							const isDescendantOfContainer = container.querySelector(`#${compId}`)
							return isDescendantOfContainer && isPropsEquals
						})
						.mapValues((currentProps, compId) => container.querySelector(`#${compId}`) as HTMLElement)
						.values()
						.value()
				},
				getComponentPropsByHtmlElement: (wrapperHtmlElement) => {
					const { id } = wrapperHtmlElement
					const compProps = propsStore.get(id)
					if (compProps) {
						return JSON.stringify(compProps)
					}

					throw new Error(
						`You are trying to get props of a DOM component (${wrapperHtmlElement.outerHTML}). ` +
							'use the node HTML attributes instead, or get props from the React component container'
					)
				},
				getComponentsByCompTypeAndProps: _.noop,
				addAllDisplayNamesToDom: _.noop,
				setSearchRoot: _.noop,
			}

			if (!window.testApi) {
				// @ts-ignore
				window.testApi = {}
			}

			window.testApi.domSelectors = domSelectors
		},
	})
)
